import { Platform, StyleSheet } from "react-native";

export const font = (w: "400" | "500" | "600" | "700") =>
  Platform.select({ web: { fontFamily: "Inter, sans-serif", fontWeight: w }, default: {} });

// Shared card shadow token (--el-01: 0px 0px 24px rgba(0,94,192,0.08))
const cardShadow = Platform.select({
  web: { boxShadow: "0px 0px 24px rgba(0,94,192,0.08)" } as any,
  default: {
    shadowColor: "#005EC0",
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.08,
    shadowRadius: 24,
    elevation: 3,
  },
});

export const styles = StyleSheet.create({
  // ── Data table card (.data-table-wrap) ─────────────────────────────────────
  dataTableWrap: {
    backgroundColor: "#fff",
    borderRadius: 8,
    overflow: "hidden",
    ...(cardShadow as object),
  },

  // ── Toolbar (.table-toolbar) ────────────────────────────────────────────────
  tableToolbar: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    paddingHorizontal: 20,
    paddingVertical: 14,
    borderBottomWidth: 1,
    borderBottomColor: "#EBEBEB",
    flexWrap: "wrap",
  },
  searchWrap: {
    flex: 1,
    minWidth: 220,
    maxWidth: 340,
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#fff",
    borderWidth: 1,
    borderColor: "#D0D0D0",
    borderRadius: 6,
    height: 36,
    paddingHorizontal: 10,
  },
  searchIcon: { marginRight: 6 },
  searchInput: {
    flex: 1,
    fontSize: 13,
    color: "#2D2D2D",
    ...font("400"),
    ...(Platform.OS === "web" ? ({ outlineWidth: 0, outlineStyle: "none" } as any) : {}),
  },
  resultCount: { fontSize: 12, color: "#656565", ...font("600") },

  // Buttons (.btn / .btn-secondary)
  btnSecondary: {
    marginLeft: "auto" as any,
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingHorizontal: 24,
    paddingVertical: 8,
    borderRadius: 100,
    borderWidth: 1,
    borderColor: "#034895",
    backgroundColor: "#fff",
    ...(Platform.OS === "web" ? ({ cursor: "pointer" } as any) : {}),
  },
  btnSecondaryText: { fontSize: 14, color: "#034895", ...font("700") },

  // ── Table (.data-table / .config-table) ────────────────────────────────────
  tableMinWidth: { minWidth: 980, width: "100%" },
  tableHeadRow: {
    flexDirection: "row",
    backgroundColor: "#fff",
    borderBottomWidth: 2,
    borderBottomColor: "#D7E5F8",
  },
  th: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 16,
    paddingVertical: 12,
    gap: 4,
    ...(Platform.OS === "web" ? ({ cursor: "pointer" } as any) : {}),
  },
  thSortTarget: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  thLabel: {
    fontSize: 12,
    color: "#4B4B4B",
    textTransform: "uppercase",
    letterSpacing: 0.4,
    ...font("700"),
  },
  sortIndicator: { fontSize: 11, color: "#4B4B4B", opacity: 0.3, ...font("700") },
  sortIndicatorActive: { opacity: 1, color: "#005CBC" },
  thFilterBtn: {
    width: 20,
    height: 20,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 4,
    backgroundColor: "transparent",
  },
  thFilterBtnActive: { backgroundColor: "#005CBC" },

  // Column widths
  colCategory: { flex: 2 },
  colSubcategory: { flex: 2 },
  colTertiary: { flex: 2 },
  colOrg: { flex: 2 },
  colAg: { flex: 1.4 },
  colTeam: { flex: 2 },
  colStatus: { flex: 1.2 },
  colActions: { width: 56, alignItems: "flex-end" },

  // Rows (tbody tr / td)
  tableBodyRow: {
    flexDirection: "row",
    alignItems: "center",
    borderBottomWidth: 1,
    borderBottomColor: "#EBEBEB",
    backgroundColor: "#fff",
  },
  tableBodyRowHovered: { backgroundColor: "#F3F7FD" },
  tableBodyRowEditing: { backgroundColor: "#E8F0FB" },
  tableBodyRowInactive: { opacity: 0.55 },
  tdCell: {
    fontSize: 13,
    color: "#4B4B4B",
    paddingHorizontal: 16,
    paddingVertical: 14,
    ...font("400"),
  },
  tdCellStrong: { color: "#2D2D2D", ...font("600") },

  // Status toggle cell — centred in its column
  toggleCell: {
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 16,
    paddingVertical: 14,
  },

  // Toggle track (.switch / .slider) — 36×20 px pill, matches HTML exactly
  toggleTrack: {
    width: 36,
    height: 20,
    borderRadius: 20,
    justifyContent: "center",
    ...(Platform.OS === "web" ? ({ cursor: "pointer" } as any) : {}),
  },
  toggleTrackOn: { backgroundColor: "#005CBC" },   // --nav-blue-700
  toggleTrackOff: { backgroundColor: "#D0D0D0" },  // --nav-gray-100
  toggleTrackDisabled: { opacity: 0.45 },

  // Toggle thumb (.slider::before) — 16×16 px circle
  toggleThumb: {
    position: "absolute",
    left: 2,
    width: 16,
    height: 16,
    borderRadius: 8,
    backgroundColor: "#ffffff",
    ...Platform.select({
      web: { boxShadow: "0 1px 3px rgba(0,0,0,0.15)" } as any,
      default: {
        shadowColor: "#000",
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.15,
        shadowRadius: 3,
        elevation: 2,
      },
    }),
  },

  actionsCell: {
    flexDirection: "row",
    justifyContent: "flex-end",
    paddingHorizontal: 12,
    paddingVertical: 14,
  },
  actionBtn: { padding: 4 },

  emptyState: { alignItems: "center", justifyContent: "center", paddingVertical: 56, gap: 8 },
  emptyStateText: { fontSize: 13, color: "#818181", ...font("400") },

  // ── Pagination (.config-pagination) ────────────────────────────────────────
  pagination: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderTopWidth: 1,
    borderTopColor: "#EBEBEB",
    backgroundColor: "#fff",
    flexWrap: "wrap",
  },
  rowsPerPage: { flexDirection: "row", alignItems: "center", gap: 8 },
  rowsPerPageLabel: { fontSize: 12, color: "#4B4B4B", ...font("600") },
  rowsPerPageSelectWrap: {
    height: 32,
    minWidth: 64,
    borderWidth: 1,
    borderColor: "#D0D0D0",
    borderRadius: 6,
    paddingHorizontal: 10,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 6,
  },
  rowsPerPageSelectText: { fontSize: 12, color: "#2D2D2D", ...font("500") },
  // Rows-per-page dropdown popover
  rppDropdown: {
    position: "absolute",
    bottom: 36,
    left: 0,
    minWidth: 64,
    backgroundColor: "#fff",
    borderWidth: 1,
    borderColor: "#D0D0D0",
    borderRadius: 6,
    zIndex: 100,
    ...Platform.select({
      web: { boxShadow: "0 4px 12px rgba(0,0,0,0.10)" } as any,
      default: {
        shadowColor: "#000",
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.10,
        shadowRadius: 8,
        elevation: 4,
      },
    }),
  },
  rppOption: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    ...(Platform.OS === "web" ? ({ cursor: "pointer" } as any) : {}),
  },
  rppOptionActive: { backgroundColor: "#EEF4FF" },
  rppOptionText: { fontSize: 12, color: "#2D2D2D", ...font("500") },
  rppOptionActiveText: { color: "#034895", ...font("700") },

  pageBtns: { flexDirection: "row", gap: 6 },
  pgButton: {
    width: 30,
    height: 30,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: "#D0D0D0",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#fff",
  },
  pgButtonActive: { backgroundColor: "#034895", borderColor: "#034895" },
  pgButtonDisabled: { opacity: 0.4 },
  pgButtonText: { fontSize: 12, color: "#4B4B4B", ...font("600") },
  pgButtonActiveText: { color: "#fff" },

  // ── Manage Teams Modal ──────────────────────────────────────────────────────

  // Semi-transparent backdrop (mirrors .modal-overlay)
  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.45)",
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
  },

  // White modal card (mirrors .modal, max-width:480px)
  modalPanel: {
    width: "100%",
    maxWidth: 480,
    backgroundColor: "#fff",
    borderRadius: 12,
    overflow: "hidden",
    ...(Platform.OS === "web"
      ? ({ boxShadow: "0 8px 32px rgba(0,0,0,0.18)" } as any)
      : {
          shadowColor: "#000",
          shadowOffset: { width: 0, height: 4 },
          shadowOpacity: 0.18,
          shadowRadius: 16,
          elevation: 8,
        }),
  },

    editModalPanel: {
      maxWidth: 576,
      borderRadius: 14,
    },
    editModalOverlay: {
      padding: 10,
    },
    editModalHeader: {
      paddingHorizontal: 28,
      paddingVertical: 20,
    },
    editModalTitle: {
      fontSize: 22,
      color: "#2D2D2D",
      ...font("700"),
    },
    editModalBody: {
      paddingHorizontal: 28,
      paddingVertical: 24,
      maxHeight: 460,
    },
    editDetailsGrid: {
      flexDirection: "row",
      flexWrap: "wrap",
      columnGap: 24,
      rowGap: 28,
    },
    editDetailCell: {
      width: "46%",
    },
    editFieldsGrid: {
      flexDirection: "row",
      gap: 24,
      marginTop: 36,
    },
    editFieldCell: {
      flex: 1,
      minWidth: 0,
    },
    editFieldLabel: {
      fontSize: 14,
      color: "#5B5B5B",
      marginBottom: 8,
      ...font("600"),
    },
    editFieldValue: {
      fontSize: 18,
      lineHeight: 23,
      color: "#454545",
      ...font("600"),
    },
    editReadOnlyText: {
      maxWidth: 220,
      marginTop: 4,
      fontSize: 13,
      lineHeight: 18,
      color: "#6B6B6B",
      ...font("400"),
    },
    editDropdown: {
      width: "100%",
    },
    editStatusRow: {
      alignItems: "flex-start",
      marginTop: 34,
      gap: 4,
    },
    editWarning: {
      marginTop: 12,
    },
    editModalFooter: {
      justifyContent: "flex-end",
      gap: 28,
      paddingHorizontal: 28,
      paddingVertical: 16,
    },
    editModalCancel: {
      paddingHorizontal: 4,
      paddingVertical: 10,
      ...(Platform.OS === "web" ? ({ cursor: "pointer" } as any) : {}),
    },
    editModalCancelText: {
      fontSize: 16,
      color: "#4B4B4B",
      ...font("600"),
    },
    editModalSave: {
      minWidth: 98,
      alignItems: "center",
      paddingHorizontal: 24,
      paddingVertical: 11,
      borderRadius: 24,
      backgroundColor: "#005CBC",
      ...(Platform.OS === "web" ? ({ cursor: "pointer" } as any) : {}),
    },
    editModalSaveDisabled: {
      backgroundColor: "#9BAAC0",
    },
    editModalSaveText: {
      fontSize: 16,
      color: "#FFFFFF",
      ...font("700"),
    },
    editConfirmOverlay: {
      ...Platform.select({
        web: { position: "absolute" } as any,
        default: { position: "absolute" as const },
      }),
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      zIndex: 100,
      alignItems: "center",
      justifyContent: "center",
      padding: 28,
      backgroundColor: "rgba(255,255,255,0.96)",
    },
    editConfirmCard: {
      width: "100%",
      maxWidth: 390,
      alignItems: "center",
    },
    editConfirmTitle: {
      fontSize: 18,
      color: "#2D2D2D",
      marginBottom: 10,
      ...font("700"),
    },
    editConfirmMessage: {
      fontSize: 14,
      lineHeight: 21,
      color: "#5B5B5B",
      textAlign: "center",
      marginBottom: 24,
      ...font("400"),
    },
    editConfirmActions: {
      flexDirection: "row",
      alignItems: "center",
      gap: 12,
    },
    editConfirmCancel: {
      paddingHorizontal: 20,
      paddingVertical: 10,
      borderRadius: 22,
      borderWidth: 1,
      borderColor: "#D0D0D0",
      backgroundColor: "#FFFFFF",
    },
    editConfirmCancelText: {
      fontSize: 14,
      color: "#4B4B4B",
      ...font("600"),
    },
    editConfirmNavigate: {
      paddingHorizontal: 20,
      paddingVertical: 10,
      borderRadius: 22,
      backgroundColor: "#005CBC",
    },
    editConfirmNavigateText: {
      fontSize: 14,
      color: "#FFFFFF",
      ...font("700"),
    },

  // Header (.modal-header)
  modalHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 24,
    paddingVertical: 18,
    borderBottomWidth: 1,
    borderBottomColor: "#EBEBEB",
  },
  modalTitle: {
    fontSize: 16,
    color: "#1A1A1A",
    ...font("700"),
  },
  modalCloseBtn: {
    width: 32,
    height: 32,
    borderRadius: 6,
    alignItems: "center",
    justifyContent: "center",
    ...(Platform.OS === "web" ? ({ cursor: "pointer" } as any) : {}),
  },

  // Body (.modal-body)
  modalBody: {
    paddingHorizontal: 24,
    paddingVertical: 16,
    maxHeight: 380,
  },
  modalHelpText: {
    fontSize: 12,
    color: "#656565",
    marginBottom: 12,
    lineHeight: 18,
    ...font("400"),
  },

  // Each team row inside the list
  teamRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: 14,
  },
  teamRowBorder: {
    borderBottomWidth: 1,
    borderBottomColor: "#EBEBEB",
  },

  // Filled pill chip (.team-pill) — matches design: tinted bg, coloured text + dot
  teamPill: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    alignSelf: "flex-start",
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 100,
  },
  teamDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
  },
  teamPillText: {
    fontSize: 14,
    ...font("700"),
  },

  // Compact pill variant for use inside table cells
  teamPillCompact: {
    paddingHorizontal: 10,
    paddingVertical: 4,
  },
  teamPillTextCompact: {
    fontSize: 12,
  },
  // Distinct from the row-level Status toggle — flags that the *assigned team* is inactive.
  teamInactiveBadge: {
    fontSize: 10,
    color: "#D0021B",
    marginTop: 3,
    ...font("600"),
  },

  // Add new team row (.teamsModalAddRow)
  addTeamRow: {
    marginTop: 14,
    gap: 6,
  },
  addTeamLabel: {
    fontSize: 12,
    color: "#656565",
    marginBottom: 2,
    ...font("600"),
  },
  addTeamInput: {
    height: 38,
    borderWidth: 1,
    borderColor: "#D0D0D0",
    borderRadius: 6,
    paddingHorizontal: 12,
    fontSize: 13,
    color: "#2D2D2D",
    ...font("400"),
    ...(Platform.OS === "web" ? ({ outlineWidth: 0, outlineStyle: "none" } as any) : {}),
  },
  addTeamInputError: {
    borderColor: "#D0021B",
  },
  validationError: {
    fontSize: 11,
    color: "#D0021B",
    ...font("600"),
  },

  // Footer (.modal-footer) — Cancel | (spacer) Add (spacer) | Save
  modalFooter: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 24,
    paddingVertical: 18,
    borderTopWidth: 1,
    borderTopColor: "#EBEBEB",
  },

  // Cancel — plain text, no border
  modalFooterCancel: {
    ...(Platform.OS === "web" ? ({ cursor: "pointer" } as any) : {}),
  },
  modalFooterCancelText: { fontSize: 15, color: "#1A1A1A", ...font("600") },

  // Add — outlined pill (border only, transparent fill)
  btnOutlinedPill: {
    paddingHorizontal: 32,
    paddingVertical: 10,
    borderRadius: 100,
    borderWidth: 1.5,
    borderColor: "#034895",
    backgroundColor: "#fff",
    ...(Platform.OS === "web" ? ({ cursor: "pointer" } as any) : {}),
  },
  btnOutlinedPillText: { fontSize: 15, color: "#034895", ...font("700") },

  // Save — filled blue pill
  btnFilledPill: {
    paddingHorizontal: 32,
    paddingVertical: 10,
    borderRadius: 100,
    backgroundColor: "#0057A8",
    ...(Platform.OS === "web" ? ({ cursor: "pointer" } as any) : {}),
  },
  btnFilledPillDisabled: { backgroundColor: "#D0D0D0" },
  btnFilledPillText: { fontSize: 15, color: "#fff", ...font("700") },
});

export default styles;
